import type { ConversationAttachmentResponse, PhraseAttachmentInPhraseResponse } from '../model/api/types';

/**
 * Возвращает «сокращённый» вариант ссылки вложения для отображения.
 *
 * Удаляет схему (`http://`, `https://`) и завершающий слеш — полный URL
 * остаётся в href/title. Для путей внутреннего хранилища (`/storage/...`)
 * возвращает строку как есть.
 */
export const shortenAttachmentUrl = (url: string): string => {
  const trimmed = url.trim();
  const withoutProtocol = trimmed.replace(/^[a-z][a-z0-9+\-.]*:\/\//i, '');
  return withoutProtocol.replace(/\/$/, '') || trimmed;
};

/**
 * Извлекает имя файла из последнего сегмента URL без query/hash.
 * Возвращает пустую строку, если осмысленное имя не определено.
 */
export const extractFilenameFromUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return '';
  // Берём часть после последнего «/», предварительно отрезав query/hash
  const withoutQuery = trimmed.split(/[?#]/, 1)[0] ?? trimmed;
  const segments = withoutQuery.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

/**
 * Возвращает текст для отображения в строке вложения.
 *
 * Приоритет источников:
 * 1. `external_raw_data.name` / `filename` / `fileName` / `file_name` — оригинальное имя файла из метаданных
 * 2. Для внутренних URL (`isExternalUrl === false`) — имя файла из последнего сегмента пути
 * 3. Сокращённый URL (без схемы) — для внешних ссылок без метаданных имени
 *
 * Полный URL остаётся в `href`/`title` для tooltip и навигации.
 */
export const getAttachmentDisplayLabel = (
  attachment: ConversationAttachmentResponse | PhraseAttachmentInPhraseResponse,
): string => {
  // externalRawData есть только у вложений уровня диалога (ConversationAttachmentResponse).
  // У вложений на фразе транскрипта (PhraseAttachmentInPhraseResponse) этого поля нет.
  const rawData = 'externalRawData' in attachment ? attachment.externalRawData : null;
  if (rawData) {
    const candidates = [rawData.name, rawData.filename, rawData.fileName, rawData.file_name];
    for (const value of candidates) {
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim();
      }
    }
  }

  const url = attachment.url ?? '';

  // Для внутренних файлов (не внешних ссылок) извлекаем имя из последнего сегмента URL.
  // Для внешних URL без метаданных имени показываем сокращённый URL (без схемы) целиком —
  // последний сегмент пути не несёт смысловой нагрузки имени файла.
  if (!attachment.isExternalUrl) {
    const fromUrl = extractFilenameFromUrl(url);
    if (fromUrl) return fromUrl;
  }

  return url ? shortenAttachmentUrl(url) : '';
};
